/**
 * Pagination Controls Component
 * Reusable pagination component with page navigation and item count display
 * @version 1.0.0
 */

import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  itemName?: string; // e.g., "alerts", "configurations"
  onPageChange: (page: number) => void;
}

/**
 * PaginationControls
 * Displays pagination controls with page numbers, navigation buttons, and item count
 *
 * Features:
 * - Accessible keyboard navigation
 * - Responsive design (mobile-friendly)
 * - Ellipsis for large page ranges
 * - Shows current range of items
 * - Disabled state for boundary pages
 */
export function PaginationControls({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  itemName = 'items',
  onPageChange,
}: PaginationControlsProps) {
  // Don't show pagination if there are no items or only one page
  if (totalItems === 0 || totalPages <= 1) {
    if (totalItems === 0) {
      return null;
    }
    // Show item count even if only one page
    return (
      <div className="flex items-center justify-center py-4">
        <div className="text-sm text-gray-600">
          Showing <span className="font-medium">1</span> to{' '}
          <span className="font-medium">{totalItems}</span> of{' '}
          <span className="font-medium">{totalItems}</span> {itemName}
        </div>
      </div>
    );
  }

  /**
   * Calculate page numbers to display
   * Shows current page ± 2 pages with ellipsis for gaps
   */
  const getPageNumbers = (): (number | null)[] => {
    const pages: (number | null)[] = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      // Show all pages if total is small
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show pages with ellipsis
      const start = Math.max(0, currentPage - 2);
      const end = Math.min(totalPages - 1, currentPage + 2);

      // Always show first page
      if (start > 0) {
        pages.push(0);
        if (start > 1) pages.push(null); // null represents ellipsis
      }

      // Show pages around current page
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      // Always show last page
      if (end < totalPages - 1) {
        if (end < totalPages - 2) pages.push(null); // ellipsis
        pages.push(totalPages - 1);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  // Calculate item range for current page
  const startItem = currentPage * pageSize + 1;
  const endItem = Math.min((currentPage + 1) * pageSize, totalItems);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-2 py-4 border-t bg-gray-50/50">
      {/* Results Info */}
      <div className="text-sm text-gray-600 order-2 sm:order-1">
        Showing <span className="font-medium">{startItem}</span> to{' '}
        <span className="font-medium">{endItem}</span> of{' '}
        <span className="font-medium">{totalItems}</span> {itemName}
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center gap-2 order-1 sm:order-2">
        {/* Previous Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 0}
          className="h-9"
          aria-label="Go to previous page"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Previous
        </Button>

        {/* Page Numbers - Hidden on mobile */}
        <div className="hidden sm:flex items-center gap-1">
          {pageNumbers.map((pageNum, idx) => {
            if (pageNum === null) {
              return (
                <span
                  key={`ellipsis-${idx}`}
                  className="px-2 text-gray-400"
                  aria-hidden="true"
                >
                  ...
                </span>
              );
            }

            const isCurrentPage = pageNum === currentPage;
            return (
              <Button
                key={pageNum}
                variant={isCurrentPage ? 'default' : 'outline'}
                size="sm"
                onClick={() => onPageChange(pageNum)}
                className="h-9 w-9"
                aria-label={`Go to page ${pageNum + 1}`}
                aria-current={isCurrentPage ? 'page' : undefined}
              >
                {pageNum + 1}
              </Button>
            );
          })}
        </div>

        {/* Mobile Page Indicator */}
        <div className="sm:hidden text-sm text-gray-600 font-medium">
          Page {currentPage + 1} of {totalPages}
        </div>

        {/* Next Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages - 1}
          className="h-9"
          aria-label="Go to next page"
        >
          Next
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}
